import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
// Import Pages
import Login from '../pages/login/login';
import Quotation from '../pages/quotation/Quotation';
import NewQuotation from '../pages/quotation/NewQuotation';
import QuotationPreviewV2 from 'pages/quotation/quotationPreviewV2';
import QuotationPreviewRedirect from 'routes/quotationPreviewRedirect';
import Hotels from '../pages/hotels/Hotels';
import HotelDetail from '../pages/hotelDetail/hotelDetail';
import Packages from '../pages/packages/Packages';
import { useLoader } from '../context/loaderContext';
import { useSnackbar } from '../context/snackbarContext';
import Loader from '../component/loader/loaderComponent';
import { agent } from '../apiClient/api';
import { useUserDetail } from 'context/userDetailContext';
import Excursions from 'pages/excursions/excursions';
import ExcursionDetail from 'pages/excursionDetail/excursionDetail';
import Places from 'pages/places/Places';
import Guides from 'pages/guides/Guides';
import Transportation from 'pages/transportation/Transportation';
import DayTripDetail from 'pages/dayTripDetail/dayTripDetail';
import PackagesDetail from 'pages/packagesDetail/packagesDetail';
import Agency from 'pages/agency';
import Configuration from 'pages/configuration/configuration';
import UserManagement from 'pages/configuration/userManagement';
import CitiesManagement from 'pages/configuration/citiesManagement';
import AdminRoute from 'pages/configuration/adminRoute';

const AppRoutes: React.FC = () => {

    const { isLoading, setIsLoading } = useLoader();
    const { showError } = useSnackbar();
    const { userDetail, setUserDetail } = useUserDetail();

    const navigate = useNavigate();

    const hasCalledApi = useRef(false);

    const getUserDetails = async (): Promise<void> => {
        setIsLoading(true);
        try {
            const response = await agent.UserMeAPI.get();
            if (response?.status === false) {
                showError('Session expired. Please login again.');
                navigate('/login');
            } else {
                setUserDetail(response?.data[0]);
            }
        } catch (error) {
            console.log("getUserDetails Error", error);
            showError('Failed to load user details');
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    }

    useEffect(() => {
        if (userDetail === null) {
            if (hasCalledApi.current) return;
            hasCalledApi.current = true;
            getUserDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {isLoading && <Loader message="Loading..." overlay={true} />}
            <Routes>
                {/* Login Route */}
                <Route path="/login" element={<Login />} />

                {/* Redirect root to login */}
                <Route path="/" element={<Navigate to="/login" replace />} />

                {/* Main Routes */}
                <Route path="/quotation" element={<Quotation />} />
                <Route path="/quotation/new" element={<NewQuotation />} />
                {/* Old preview URL redirects to the new Quotation Preview */}
                <Route path="/quotation/view/:quotation_id" element={<QuotationPreviewRedirect />} />
                <Route path="/quotation/preview/:quotation_id" element={<QuotationPreviewV2 />} />
                <Route path="/hotels" element={<Hotels />} />
                <Route path="/hotels/:hotel_id" element={<HotelDetail />} />
                <Route path="/hotels/new" element={<HotelDetail />} />
                <Route path="/packages" element={<Packages />} />
                <Route path="/agency" element={<Agency />} />
                <Route path="/excursions" element={<Excursions />} />
                <Route path="/excursions/:excursion_id" element={<ExcursionDetail />} />
                <Route path="/places" element={<Places />} />
                <Route path="/guides" element={<Guides />} />
                <Route path="/transportation" element={<Transportation />} />
                <Route path="/transportation/day-trip/new" element={<DayTripDetail />} />
                <Route path="/transportation/day-trip/:day_trip_id" element={<DayTripDetail />} />
                <Route path="/transportation/package/new" element={<PackagesDetail />} />
                <Route path="/transportation/package/:package_id" element={<PackagesDetail />} />
                <Route
                    path="/configuration"
                    element={
                        <AdminRoute>
                            <Configuration />
                        </AdminRoute>
                    }
                >
                    <Route index element={<Navigate to="users" replace />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="cities" element={<CitiesManagement />} />
                </Route>

                {/* 404 - Not Found */}
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        </>
    );
};

export default AppRoutes;

